/**
 * TagTable - Tabela de etiquetas usando DataTable genérico
 * Mantém funcionalidade específica: exibição de cor
 * Princípios: DRY (reutiliza DataTable), SRP (apenas configuração de colunas)
 */
import React, { useMemo } from 'react';
import { Tag } from '../../../../types';
import DataTable, { Column } from '../../../../components/shared/DataTable';
import ActionMenu, { createEditAction, createDeleteAction, createDuplicateAction } from '../../../../components/shared/ActionMenu';

interface TagTableProps {
    tags: Tag[];
    isLoading?: boolean;
    onEdit: (tag: Tag) => void;
    onDelete: (tag: Tag) => void;
    onDuplicate: (tag: Tag) => void;
    selectedIds?: Set<string>;
    onToggleSelection?: (id: string) => void;
    onToggleAll?: () => void;
}

const TagTable: React.FC<TagTableProps> = ({
    tags, isLoading, onEdit, onDelete, onDuplicate,
    selectedIds = new Set(), onToggleSelection, onToggleAll
}) => {
    const columns: Column<Tag>[] = useMemo(() => [
        {
            key: 'name', header: 'Nome',
            render: (tag) => <span className="font-medium text-default">{tag.name}</span>
        },
        {
            key: 'color', header: 'Cor',
            render: (tag) => (
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border border-subtle" style={{ backgroundColor: tag.color }} />
                    <span className="text-muted uppercase text-xs">{tag.color}</span>
                </div>
            )
        },
        {
            key: 'actions', header: 'Ações', align: 'right',
            render: (tag) => (
                <ActionMenu actions={[
                    createEditAction(() => onEdit(tag)),
                    createDuplicateAction(() => onDuplicate(tag)),
                    createDeleteAction(() => onDelete(tag))
                ]} />
            )
        }
    ], [onEdit, onDuplicate, onDelete]);

    return (
        <DataTable
            columns={columns}
            data={tags}
            getRowKey={(t) => t.id}
            emptyMessage="Nenhuma etiqueta encontrada."
            isLoading={isLoading}
            selectable
            selectedIds={selectedIds}
            onToggleSelection={onToggleSelection}
            onToggleAll={onToggleAll}
        />
    );
};

export default TagTable;
